import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Switch, Route, useHistory } from 'react-router-dom';
import store from 'store/index';
import api from 'api';

import Loading from 'components/Loading/index';
import Header from 'components/Header';
import Plan from 'components/Plan/index';
import Support from 'components/Support/index';
import User from 'components/User/index';
import RecipeList from 'components/Recipe/RecipeList';
import RecipeEditor from 'components/Recipe/RecipeEditor/RecipeEditor';
import RecipeShare from 'components/RecipeShare/index';
import RecipeSaveToMyRecipes from 'components/RecipeSaveToMyRecipes/index';
import ProfileCreate from 'components/Profiles/ProfileCreate/index';

import SimulateLogin from 'components/SimulateLogin/index';
import LectureIntro from 'components/LectureIntro/index';
import LectureTaskLoad from 'components/LectureTaskLoad/index';
import LectureSeminarWorkTaskLoad from 'components/LectureSeminarWorkTaskLoad/index';

import { loginUser, setAppMode, setAppModeData, loadGlobals, showMessage, closeModal } from 'actions/index';

function Main() {
    const data = useSelector(state => state);
    const dispatch = useDispatch();
    const history = useHistory();
    const [initialized, setInitialized] = useState(false);
    const [modalClosing, setModalClosing] = useState(false);
    const modalTargetRef = useRef(null);

    useEffect(() => {
        const state = store.getState();
        const mode = localStorage.getItem('zofapp_mode');

        if (mode === 'LECTURE' || mode === 'NUTRIKUCHAR') {
            const lectureId = localStorage.getItem('zofapp_mode_data') * 1;
            if (lectureId > 0) {
                dispatch(setAppMode(mode));
                dispatch(setAppModeData({ lectureId }));
            }
        }

        if (window.location.pathname === '/simulate-login') {
            setInitialized(true);
            return;
        }

        if (state.user === false) {
            const jwtToken = localStorage.getItem('jwt_token');
            if (!jwtToken || jwtToken === '') {
                history.push('/login');
                return;
            }

            api('check-token').then(result => {
                if (result.data.valid === 0) {
                    history.push('/login');
                    return;
                }
                api('load/startup-data').then(result => {
                    dispatch(loadGlobals(result.data));
                    dispatch(loginUser({
                        user: result.data.user,
                        callback: () => setInitialized(true)
                    }));
                });
            });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!initialized) return;
        const profiles = data.globals.profiles;
        if (typeof profiles !== 'object') return;
        if (Object.keys(profiles).length === 0 && window.location.pathname !== '/profiles/create') {
            history.push('/profiles/create');
        }
    }, [initialized, data.globals.profiles]); // eslint-disable-line react-hooks/exhaustive-deps

    const triggerClose = useCallback(() => {
        const cb = data.modalOnClose;
        setModalClosing(true);
        setTimeout(() => {
            dispatch(closeModal());
            setModalClosing(false);
            if (typeof cb === 'function') cb();
        }, 160);
    }, [data.modalOnClose, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCloseModal = (e) => {
        e.stopPropagation();
        if (modalTargetRef.current === e.currentTarget) {
            triggerClose();
        }
    };

    require('styles/index.css');

    if (!initialized) return <Loading />;

    let message = null;
    if (data.message.show) {
        const isError = data.message.type === 'error';
        const msgClass = `fixed flex items-center top-[1em] left-1/2 -translate-x-1/2 z-[10000010] px-6 py-6 shadow-[0_4px_16px_rgba(0,0,0,0.2)] rounded-xl [animation:slideDown_0.2s_normal_forwards_ease-in] ${isError ? 'bg-[#ff1144] text-white' : 'bg-white'}`;
        const iconClass = `mr-[0.5em] ${data.message.type === 'info' ? 'text-[#777]' : data.message.type === 'success' ? 'text-[#0c0]' : 'text-white'}`;
        message = (
            <div className={msgClass}>
                {data.message.type === 'info' && <i className={`fa fa-info-circle ${iconClass}`}></i>}
                {data.message.type === 'error' && <i className={`fa fa-exclamation-triangle ${iconClass}`}></i>}
                {data.message.type === 'success' && <i className={`fa fa-check-circle ${iconClass}`}></i>}
                <p className="m-0">{data.message.message}</p>
                <a className={`ml-4 ${isError ? 'text-white' : 'text-[#aaa]'}`} onClick={() => dispatch(showMessage({ show: false }))}><i className="fa fa-times"></i></a>
            </div>
        );
    }

    let modal = null;
    if (data.modalOpened) {
        const mc = data.modalClass;
        const closing = modalClosing;

        const backdropAnim = closing
            ? '[animation:backdropLeave_0.16s_ease-in_forwards]'
            : '[animation:backdropEnter_0.18s_ease-out]';
        const bodyAnim = closing
            ? '[animation:modalLeave_0.16s_ease-in_forwards]'
            : '[animation:modalEnter_0.22s_cubic-bezier(0.34,1.56,0.64,1)]';

        const bodyBase = `bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col ${bodyAnim}`;
        let bodyClass = `${bodyBase} w-full max-w-[480px] max-h-[90vh]`;
        if (mc.includes('dialog-big')) bodyClass = `${bodyBase} w-[85vw] h-[85vh] max-w-[1080px] max-[639px]:w-[calc(100vw-1.5rem)]`;
        else if (mc.includes('dialog-flexible')) bodyClass = `${bodyBase} w-[calc(100vw-1.5rem)] max-w-[680px] max-h-[90vh]`;

        const contentClass = mc.includes('dialog-content-nopadding')
            ? 'flex flex-col flex-1 overflow-y-auto'
            : 'flex flex-col flex-1 px-5 py-4 overflow-y-auto';

        modal = (
            <div
                className={`fixed inset-0 flex items-center justify-center z-[10000000] ${backdropAnim}`}
                style={{ background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(3px)' }}
                onMouseDown={e => { modalTargetRef.current = e.target; }}
                onClick={handleCloseModal}
            >
                <div className={bodyClass}>
                    {/* Header */}
                    <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 shrink-0">
                        <h2 className="text-base font-semibold text-slate-800 m-0 flex-1 leading-snug">
                            {data.modalTitle}
                        </h2>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); triggerClose(); }}
                            className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-400 hover:bg-slate-200 hover:text-slate-600 transition-colors flex-shrink-0 cursor-pointer border-none"
                        >
                            <i className="fa fa-times text-xs"></i>
                        </button>
                    </div>

                    {/* Content */}
                    <div className={contentClass}>
                        {data.modalContent}
                        {data.closeButton !== '' && (
                            <div className="pt-4 pb-1 text-center">
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); triggerClose(); }}
                                    className="inline-block bg-green-500 text-white text-sm font-semibold py-2.5 px-8 rounded-xl hover:bg-green-600 transition-colors cursor-pointer border-none"
                                >
                                    {data.closeButton}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    require('styles/index.css');

    const profiles = data.globals.profiles;
    const hasNoProfiles = initialized && typeof profiles === 'object' && Object.keys(profiles).length === 0;

    return (
        <>
            <Header />
            <main role="main" className="bg-white flex-1 flex">
                <Switch>
                    <Route path="/profiles/create" exact={true} component={ProfileCreate} />
                    {hasNoProfiles && <Route path="*" component={ProfileCreate} />}

                    <Route path="/simulate-login" component={SimulateLogin} />
                    <Route path="/" exact={true} component={LectureIntro} />
                    <Route path="/plan" exact={true} component={Plan} />
                    <Route path="/load/task/:id/:lectureId" component={LectureTaskLoad} />
                    <Route path="/load/seminarwork-task/:lectureId" component={LectureSeminarWorkTaskLoad} />
                    <Route path="/recipe/edit/meal/:hash" render={(props) => <RecipeEditor {...props} action="lecture-task" />} />
                    <Route path="/user" component={User} />
                    <Route path="/support" exact={true} component={Support} />
                    <Route path="/recipe" exact={true} component={RecipeList} />
                    <Route path="/recipe/open/:id" render={(props) => <RecipeEditor {...props} />} />
                    <Route path="/recipe/create" exact={true} render={(props) => <RecipeEditor {...props} action="recipe-new" />} />
                    <Route path="/recipe/share/:id/:profile" component={RecipeShare} />
                    <Route path="/recipe/save-to-my-recipes/:hash" component={RecipeSaveToMyRecipes} />
                    <Route path="/recipe/:action" component={RecipeList} />
                </Switch>
            </main>
            {message}
            {modal}
        </>
    );
}

export default Main;
